import { useEffect } from 'react'
import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { FormattedMessage } from 'react-intl'
import { NavLink } from 'react-router-dom'
import { isEmpty } from 'lodash'

import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { isFilteringByInStockValue, IN_STOCK } from 'features/Snacks/components/InStockFilter/constants'
import { filterEmptyValue } from 'utils/object'

const LIMIT = 30

const GET_INVENTORY_ITEMS = gql`
  query GetItems(
    $limit: Int!
    $page: Int!
    $category: String
    $subcategory: String
    $itemId: ID
    $inventoryId: ID!
    $inStock: Boolean
  ) {
    inventoryItems(
      pagination: { size: $limit, page: $page }
      input: {
        inventoryID: $inventoryId
        category: $category
        subcategory: $subcategory
        itemID: $itemId
        inStock: $inStock
      }
    ) {
      total
      items {
        id
        name
        photoPath
        netContent
        units
        container
        category
        subcategory
        needsRefrigeration
        sellingPrice
        minSellingPrice
        stockQuantity
        soldQuantity
        lostQuantity
        isActive
      }
    }
  }
`

export const withInventoryItems =
  (List: TSFixMe, Pagination: TSFixMe): TSFixMe =>
  // eslint-disable-next-line react/display-name
  (props: TSFixMe): TSFixMe => {
    const variables = {} as TSFixMe

    if (isFilteringByInStockValue(props.filters.inStock)) {
      variables.inStock = props.filters.inStock === IN_STOCK.WITH_PRODUCTS
    }
    const appliedFilters = filterEmptyValue({
      itemId: props.filters.item && props.filters.item.id,
      category: props.filters.category,
      subcategory: props.filters.subcategory,
      inStock: props.filters.inStock !== IN_STOCK.WITH_PRODUCTS ? true : null
    })
    const {
      loading,
      error,
      data = {},
      refetch,
      networkStatus
    } = useQuery(GET_INVENTORY_ITEMS, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      variables: {
        limit: LIMIT,
        page: props.page - 1,
        ...variables,
        ...filterEmptyValue({
          itemId: props.filters.item && props.filters.item.id,
          category: props.filters.category,
          subcategory: props.filters.subcategory,
          inventoryId: props.filters.cartId
        })
      }
    })

    useEffect((): TSFixMe => {
      refetch()
    }, [])

    if (networkStatus === NetworkStatus.refetch || loading) {
      return (
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      )
    } else if (error) {
      return (
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="spanish.inventory.items.error.description"
              defaultMessage="Hubo un error al cargar tus items"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button onClick={(): TSFixMe => refetch()} kind="primary">
              <FormattedMessage id="spanish.inventory.items.error.action" defaultMessage="Recargar los items" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      )
    } else if (isEmpty(data.inventoryItems.items) && props.page === 1 && isEmpty(appliedFilters)) {
      return (
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage id="spanish.inventory.items.empty.description" defaultMessage="No tienes items todavía" />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button as={NavLink} to="/snacks/products/new" kind="primary">
              <FormattedMessage id="spanish.inventory.items.empty.action" defaultMessage="Agregar item" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      )
    } else if (isEmpty(data.inventoryItems.items)) {
      return (
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="spanish.inventory.items.empty.filters.description"
              defaultMessage="No se encontró items"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button as={NavLink} to={`/carts/${props.cartId}/items`} kind="primary">
              <FormattedMessage id="spanish.inventory.items.empty.filters.action" defaultMessage="Borrar filtros" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      )
    } else {
      return (
        <>
          <List items={data.inventoryItems.items} {...props} />

          <Pagination
            totalItems={data.inventoryItems.total}
            currentItemsCount={data.inventoryItems.items.length}
            {...props}
          />
        </>
      )
    }
  }
